import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { trackDesignSystemAction } from 'analytics';
import { DsTabKind } from 'designSystem/types';
import { CHILDLESS_TAB_KINDS, DS_TAB_CHROME, buildTabId } from 'designSystem/util';
import {
	dsPickToNavTarget,
	getTokenCategoryFromPick,
	symbolDisplayName,
} from 'designSystem/content/util';
import type { DesignSystemContent } from 'designSystem/content/types';
import type { DsPick } from 'dragonmouse-shared/interfaces';
import {
	addDsTab,
	setActiveDsTab,
	setDesignSystemFilterCategories,
	updateDsTab,
} from 'redux/mode/actions';
import { displayAlertModal } from 'redux/mode/util';
import store from 'redux/store';

// Derives a tab label from kind + contentPath by looking up the content
function resolveTabLabel(
	kind: DsTabKind,
	contentPath: string | undefined,
	content: DesignSystemContent | null
): string {
	const chrome = DS_TAB_CHROME[kind];
	if (!contentPath) return chrome?.label ?? kind;

	if (kind === 'components') {
		return content?.components?.byId?.[contentPath]?.name ?? symbolDisplayName(contentPath);
	}
	if (kind === 'guidelines') {
		const guideline = content?.guidelines?.find((g) => g.path === contentPath);
		return guideline?.title ?? symbolDisplayName(contentPath);
	}
	if (kind === 'gallery') {
		const gallery = content?.gallery?.find((g) => g.path === contentPath);
		return gallery?.title ?? symbolDisplayName(contentPath);
	}

	return chrome?.label ?? kind;
}

// Checks whether a detail-tab content item exists in the current DS content
function contentExists(
	kind: DsTabKind,
	contentPath: string,
	content: DesignSystemContent | null
): boolean {
	if (kind === 'components') return !!content?.components?.byId?.[contentPath];
	if (kind === 'guidelines') return !!content?.guidelines?.find((g) => g.path === contentPath);
	if (kind === 'gallery') return !!content?.gallery?.find((g) => g.path === contentPath);
	return true;
}

// Centralizes DS tab navigation: tab creation/activation
// and the assets single-tab convention.
const useDsNavigation = () => {
	const dispatch = useDispatch();

	// Opens or activates a DS tab. When background is true, the tab is
	// created but the current active tab is preserved (Cmd-click behaviour).
	const openTab = useCallback(
		(kind: DsTabKind, contentPath?: string, background?: boolean) => {
			trackDesignSystemAction('navigate_section', { section: kind, contentPath });
			const chrome = DS_TAB_CHROME[kind];
			if (!chrome) return;

			const state = store.getState();
			const dsTabs = state.mode.dsTabs;
			const previousActiveTabId = state.mode.activeDsTabId;

			const tabId = buildTabId(kind, contentPath);

			if (kind === 'assets') {
				const existingTab = dsTabs.find((t) => t.id === tabId);
				if (existingTab) {
					if (contentPath) dispatch(updateDsTab({ id: tabId, contentPath }));
					if (!background) dispatch(setActiveDsTab(tabId));
				} else {
					dispatch(addDsTab({ id: tabId, kind, contentPath, label: chrome.label }));
					if (background) dispatch(setActiveDsTab(previousActiveTabId));
				}
			} else if (CHILDLESS_TAB_KINDS.has(kind)) {
				const existingTab = dsTabs.find((t) => t.id === tabId);
				if (existingTab) {
					if (!background) dispatch(setActiveDsTab(tabId));
				} else {
					dispatch(addDsTab({ id: tabId, kind, label: chrome.label }));
					if (background) dispatch(setActiveDsTab(previousActiveTabId));
				}
			} else {
				const content = state.mode.designSystemContent;
				const existingTab = dsTabs.find((t) => t.id === tabId);
				if (existingTab) {
					if (!background) dispatch(setActiveDsTab(tabId));
				} else if (contentPath && !contentExists(kind, contentPath, content)) {
					displayAlertModal({
						title: 'Item not found',
						description:
							'This item was renamed or removed from the design system and is no longer available.',
						submitLabel: 'OK',
						onSubmit: () => {},
					});
					return;
				} else {
					dispatch(
						addDsTab({
							id: tabId,
							kind,
							contentPath,
							label: resolveTabLabel(kind, contentPath, content),
							showBackButton: !!contentPath,
						})
					);
					if (background) dispatch(setActiveDsTab(previousActiveTabId));
				}
			}
		},
		[dispatch]
	);

	// Opens (or activates) a tab to show the provided DsPick.
	// For token picks that land on the Styles tab, also focuses the matching
	// category via the filter pills — Colors and Typography don't have pills
	// so no filter is needed there.
	const openPickTab = useCallback(
		(pick: DsPick) => {
			const content = store.getState().mode.designSystemContent;
			const target = dsPickToNavTarget(pick, content);
			if (!target) return;
			openTab(target.kind, target.contentPath);

			if (target.kind === 'styles' && pick.kind === 'token') {
				const category = getTokenCategoryFromPick(pick.cssVariable, content);
				if (category) {
					dispatch(
						setDesignSystemFilterCategories({
							section: 'styles',
							categories: [category],
						})
					);
				}
			}
		},
		[openTab, dispatch]
	);

	return { openTab, openPickTab };
};

export default useDsNavigation;
